import React, { useState, useEffect } from "react";

import { css, cx } from "@emotion/css";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";

import { useStyles } from "@/hooks/useStyles.ts";
import type { Theme } from "@/theme/CreateTheme.ts";
import type { HTMLAutocomplete } from "@/uiKit/atoms/inputs/AutoComplete.ts";
import { TextInput } from "@/uiKit/atoms/inputs/TextInput.tsx";
import { AtomProps, StyleClasses } from "@/uiKit/atoms/utils/AtomTypes.ts";
import { toDateTime } from "@/utils/dateUtils.ts";

export const DATE_INPUT_FORMAT = "dd/LL/yyyy";

export type BaseDateInputProps = {
  value: string | undefined;
  label?: string;
  onChange?: (value: string | undefined, event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  onFocus?: (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  autoFocus?: boolean;
  placeholder?: string;
  disabled?: boolean;
  error?: boolean;
  startAdornment?: React.ReactNode;
  endAdornment?: React.ReactNode;
  autoComplete?: HTMLAutocomplete;
};

export type DateInputProps = AtomProps<BaseDateInputProps, StyleClasses<"root">>;

const manageSlashes = (oldValue: string, newValue: string) => {
  if (!newValue) {
    return newValue;
  }
  const isDeleting = !!oldValue && oldValue.length > newValue.length;
  if (isDeleting && oldValue.endsWith("/")) {
    // If deleted a slash do nothing or multiple characters
    return newValue;
  }
  if (isDeleting && newValue.endsWith("/")) {
    // If deleted a character after a slash => delete the slash
    return newValue.slice(0, -1);
  }
  if (newValue.match(/^[\d/]{2,10}$/)) {
    const numbers = newValue.replaceAll("/", "");
    const dayPart = numbers.slice(0, 2);
    const hasDay = dayPart.length === 2;
    const monthPart = numbers.slice(2, 4);
    const hasMonth = monthPart.length === 2;
    const yearPart = numbers.slice(4, 8);
    return `${dayPart}${hasDay ? "/" : ""}${monthPart}${hasMonth ? "/" : ""}${yearPart}`;
  }

  return newValue;
};

export const DateInput: React.FC<DateInputProps> = ({
  value,
  onChange,
  onBlur,
  onFocus,
  error,
  startAdornment,
  endAdornment,
  className,
  classes,
  ...props
}) => {
  const styles = useStyles(makeStyles);
  const { t } = useTranslation();
  const [rawValue, setRawValue] = useState<string>("");

  useEffect(() => {
    const dt = toDateTime(value);
    const newValue = dt.isValid && dt.toFormat(DATE_INPUT_FORMAT);
    if (newValue) {
      setRawValue(newValue);
    }
  }, [value]);

  return (
    <TextInput
      value={rawValue}
      onChange={(value, event) => {
        const newValue = manageSlashes(rawValue, value ?? "");
        setRawValue(newValue);
        const dt = DateTime.fromFormat(newValue, DATE_INPUT_FORMAT);
        if (dt.isValid) {
          onChange?.(dt.toISODate(), event);
        } else {
          onChange?.("", event);
        }
      }}
      placeholder={t("form.date.format")}
      onBlur={onBlur}
      onFocus={onFocus}
      className={cx("scrollbar", className)}
      error={error}
      classes={{
        root: cx(styles.root, classes?.root),
      }}
      startAdornment={startAdornment}
      endAdornment={endAdornment}
      {...props}
    />
  );
};

const makeStyles = (theme: Theme) => ({
  root: css({
    "& .MuiPickersSectionList-root": {
      width: theme.spacing(64),
    },
  }),
});
